import { useState, useCallback } from 'react';
import supabase from '@/supabase';

interface StartConversationResult {
  success: boolean;
  conversation_id?: string;
  created?: boolean;
  error?: string;
  message?: string;
}

interface UseStartConversationReturn {
  startConversation: (otherUserId: string) => Promise<StartConversationResult>;
  isLoading: boolean;
  error: string | null;
}

export function useStartConversation(userId: string | undefined): UseStartConversationReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startConversation = useCallback(async (otherUserId: string): Promise<StartConversationResult> => {
    if (!userId) {
      return {
        success: false,
        error: 'not_authenticated',
        message: 'User must be logged in to start a conversation',
      };
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('start_or_get_conversation', {
        p_user_id: userId,
        p_other_user_id: otherUserId,
      });

      if (rpcError) {
        const errorMessage = rpcError.message || 'Failed to start conversation';
        setError(errorMessage);
        return {
          success: false,
          error: 'rpc_error',
          message: errorMessage,
        };
      }

      // The RPC returns a JSONB object
      const result = data as StartConversationResult;

      if (!result.success) {
        setError(result.message || 'Failed to start conversation');
        return result;
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      return {
        success: false,
        error: 'unexpected_error',
        message: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  return {
    startConversation,
    isLoading,
    error,
  };
}
